import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity
} from "react-native";
import NavBar from "src/components/navBar";
import Glob from "src/globalConstants";
import Analytics from "src/backend/analytics";
import TCLogoIcon from "src/components/TCLogoIcon";
import Moment from "moment";

const { width } = Glob.get("dimensions");

export default function TCConferenceReport({ route, navigation }) {
  const { report, childName } = route.params;
  const [loading] = useState(false);

  useEffect(() => {
    Analytics.logEvent("view_tcConferenceReport");
  }, []);

  const formatDate = (dateString) => {
    return Moment(dateString).format("MMM D, YYYY");
  };

  // Handle lesson press navigation
  const handleLessonPress = (lesson) => {
    Analytics.logEvent("touch_tcConferenceReport_lesson", {
      lessonID: lesson.id,
      lessonName: lesson.name
    });

    navigation.push("tcLessonDetails", {
      lessonID: lesson.id,
      childID: report.child_id
    });
  };

  // Render different component types based on their type
  const renderComponent = (component, index) => {
    const {
      type,
      data,
      title,
      label,
      skills,
      columns,
      width: componentWidth
    } = component;

    switch (type) {
      case "SchoolBanner":
        return (
          <View
            key={index}
            style={[
              styles.bannerContainer,
              component.pull_right && styles.pullRight
            ]}
          >
            {data?.logo && data.logo !== "missing_original.png" && (
              <Image
                source={{ uri: data.logo }}
                style={[
                  styles.schoolLogo,
                  componentWidth && {
                    width: width * (parseInt(componentWidth, 10) / 100)
                  }
                ]}
                resizeMode="contain"
              />
            )}
          </View>
        );

      case "ChildName":
      case "ChildAge":
      case "ChildBirthday":
      case "Classroom":
      case "Author":
      case "EndDate":
      case "Parents":
      case "StudentId":
      case "StartToEndDate":
      case "Session":
      case "ChildGrade":
      case "ChildFirstSession":
      case "ChildAgeAtSessionStart":
        return (
          <View key={index} style={styles.infoRow}>
            <Text style={styles.infoLabel}>{label || title}:</Text>
            <Text style={styles.infoValue}>{data?.value || "N/A"}</Text>
          </View>
        );

      case "AttendanceCount":
        return (
          <View key={index} style={styles.infoRow}>
            <Text style={styles.infoLabel}>{label}:</Text>
            <Text style={styles.infoValue}>{data?.value || "0"}</Text>
          </View>
        );

      case "SkillsTable":
        return (
          <View key={index} style={styles.skillsTableContainer}>
            <Text style={styles.skillsTableTitle}>{title}</Text>
            <View style={styles.skillsTable}>
              {/* Header Row */}
              <View style={styles.skillsHeaderRow}>
                <View style={styles.skillsHeaderSkillCell}>
                  <Text style={styles.skillsHeaderText}>Skill</Text>
                </View>
                {columns?.map((column, colIndex) => (
                  <View key={colIndex} style={styles.skillsHeaderCell}>
                    <Text style={styles.skillsHeaderText}>{column}</Text>
                  </View>
                ))}
              </View>

              {/* Skills Rows */}
              {skills?.map((skill, skillIndex) => (
                <View key={skillIndex} style={styles.skillsRow}>
                  <View style={styles.skillsSkillCell}>
                    <Text style={styles.skillsSkillText}>{skill}</Text>
                  </View>
                  {columns?.map((_, colIndex) => (
                    <View key={colIndex} style={styles.skillsCell}>
                      <Text style={styles.skillsCellText}>
                        {data?.scores?.[skillIndex]?.[colIndex] || ""}
                      </Text>
                    </View>
                  ))}
                </View>
              ))}
            </View>
          </View>
        );

      case "LessonListWithProficiencies":
        return (
          <View key={index} style={styles.lessonsContainer}>
            <Text style={styles.lessonsTitle}>Lessons and Proficiencies</Text>
            {data?.groups?.map((group, groupIndex) => (
              <View key={groupIndex} style={styles.lessonGroup}>
                <Text style={styles.lessonGroupTitle}>{group.name}</Text>
                {group.description && (
                  <Text style={styles.lessonGroupDescription}>
                    {group.description}
                  </Text>
                )}
                {/* Display group note if it exists and is not empty */}
                {data?.notes?.[group.id] && data.notes[group.id].trim() !== "" && (
                  <View style={styles.lessonGroupNoteContainer}>
                    <Text style={styles.lessonGroupNoteText}>
                      {data.notes[group.id]}
                    </Text>
                  </View>
                )}
                {renderLessonChildren(group.children || [])}
              </View>
            ))}
          </View>
        );

      case "Note":
        return (
          <View key={index} style={styles.noteContainer}>
            {title && <Text style={styles.noteTitle}>{title}</Text>}
            <Text style={styles.noteText}>{data?.value || ""}</Text>
          </View>
        );

      case "Divider":
        return <View key={index} style={styles.divider} />;

      case "ProfilePhoto":
        return (
          <View key={index} style={styles.profilePhotoContainer}>
            {data?.logo && (
              <Image
                source={{ uri: data.logo }}
                style={styles.profilePhoto}
                resizeMode="cover"
              />
            )}
          </View>
        );

      case "ChoosePhoto":
        return (
          <View key={index} style={styles.photoContainer}>
            {data?.photo && (
              <Image
                source={{ uri: data.photo }}
                style={[
                  styles.chosenPhoto,
                  componentWidth && {
                    width: width * (parseInt(componentWidth, 10) / 100)
                  }
                ]}
                resizeMode="cover"
              />
            )}
            {data?.caption && (
              <Text style={styles.photoCaption}>{data.caption}</Text>
            )}
          </View>
        );

      case "ReportTitle":
        return (
          <View key={index} style={styles.titleContainer}>
            <Text style={styles.reportTitle}>{title}</Text>
          </View>
        );

      case "Heading":
        return (
          <View key={index} style={styles.headingContainer}>
            <Text style={styles.heading}>{title}</Text>
          </View>
        );

      case "Subheading":
        return (
          <View key={index} style={styles.subheadingContainer}>
            <Text style={styles.subheading}>{title}</Text>
          </View>
        );

      case "Panel":
        return (
          <View key={index} style={styles.panelContainer}>
            {title && <Text style={styles.panelTitle}>{title}</Text>}
            <Text style={styles.panelText}>
              {component.text || data?.value || ""}
            </Text>
          </View>
        );

      case "Text":
        return (
          <View key={index} style={styles.textContainer}>
            <Text style={styles.plainText}>{component.text || ""}</Text>
          </View>
        );

      case "PageBreak":
        return <View key={index} style={styles.pageBreak} />;

      case "Key":
        return (
          <View key={index} style={styles.keyContainer}>
            <Text style={styles.keyTitle}>Key:</Text>
            {component.values?.map((value, valueIndex) => (
              <Text key={valueIndex} style={styles.keyItem}>
                {value}: {component.descriptions?.[valueIndex]}
              </Text>
            ))}
          </View>
        );

      default:
        return null;
    }
  };

  const renderLessonChildren = (children, level = 0) => {
    return children.map((child, childIndex) => (
      <View
        key={childIndex}
        style={[styles.lessonChild, { marginLeft: level * 20 }]}
      >
        <Text style={styles.lessonChildName}>{child.name}</Text>

        {/* Render lessons */}
        {child.lessons?.map((lesson, lessonIndex) => (
          <TouchableOpacity
            key={lessonIndex}
            style={styles.lessonItem}
            onPress={() => handleLessonPress(lesson)}
            activeOpacity={0.7}
          >
            <View style={styles.lessonContent}>
              <Text style={styles.lessonName}>{lesson.name}</Text>
              <View
                style={[
                  styles.proficiencyBadge,
                  { backgroundColor: getProficiencyColor(lesson.proficiency) }
                ]}
              >
                <Text style={styles.proficiencyText}>
                  {lesson.proficiency_name}
                </Text>
              </View>
            </View>
          </TouchableOpacity>
        ))}

        {/* Recursively render nested children */}
        {child.children && child.children.length > 0 && (
          <View>{renderLessonChildren(child.children, level + 1)}</View>
        )}
      </View>
    ));
  };

  const getProficiencyColor = (proficiency) => {
    switch (proficiency) {
      case 0:
        return "#ffe7cc"; // Needs More
      case 1:
        return "#f0fbff"; // Introduced
      case 2:
        return "#b0dff4"; // Practiced?
      case 3:
        return "#4ecdc4"; // Not sure, maybe Developing?
      case 4:
        return "#b0dff4"; // Practiced
      case 5:
        return "#9abdd2"; // Mastered
      default:
        return "#e0e0e0"; // Default
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <NavBar navigation={navigation} text="Conference Report" />
        <View style={styles.loadingContainer}>
          <TCLogoIcon size={40} spinning />
          <Text style={styles.loadingText}>Loading report...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <NavBar navigation={navigation} text="Conference Report" />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Report Header */}
        <View style={styles.headerSection}>
          <Text style={styles.reportName}>{report.name}</Text>
          <Text style={styles.reportDate}>
            Created: {formatDate(report.created_at)}
          </Text>
          <Text style={styles.childName}>Student: {childName}</Text>
        </View>

        {/* Report Content */}
        <View style={styles.reportContent}>
          {report.data?.map((component, index) =>
            renderComponent(component, index)
          )}
        </View>

        {/* Bottom spacing */}
        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    backgroundColor: "#f5f7fa"
  },
  scrollView: {
    flex: 1,
    width
  },
  scrollContent: {
    padding: 16
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: "#666"
  },
  headerSection: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2
  },
  reportName: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#212529",
    marginBottom: 8
  },
  reportDate: {
    fontSize: 16,
    color: "#6c757d",
    marginBottom: 4
  },
  childName: {
    fontSize: 16,
    color: "#6c757d"
  },
  reportContent: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2
  },
  bannerContainer: {
    alignItems: "center",
    marginBottom: 16
  },
  pullRight: {
    alignItems: "flex-end"
  },
  schoolLogo: {
    height: 60,
    width: width * 0.25,
    maxWidth: 200
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0"
  },
  infoLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#495057",
    flex: 1
  },
  infoValue: {
    fontSize: 16,
    color: "#212529",
    flex: 1,
    textAlign: "right"
  },
  skillsTableContainer: {
    marginVertical: 20
  },
  skillsTableTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#212529",
    marginBottom: 12,
    textAlign: "center"
  },
  skillsTable: {
    borderWidth: 1,
    borderColor: "#dee2e6",
    borderRadius: 8,
    overflow: "hidden"
  },
  skillsHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#e9ecef"
  },
  skillsHeaderSkillCell: {
    flex: 2,
    padding: 12,
    borderRightWidth: 1,
    borderRightColor: "#dee2e6"
  },
  skillsHeaderCell: {
    flex: 1,
    padding: 12,
    borderRightWidth: 1,
    borderRightColor: "#dee2e6",
    alignItems: "center"
  },
  skillsHeaderText: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#495057",
    textAlign: "center"
  },
  skillsRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#dee2e6"
  },
  skillsSkillCell: {
    flex: 2,
    padding: 12,
    borderRightWidth: 1,
    borderRightColor: "#dee2e6",
    justifyContent: "center"
  },
  skillsCell: {
    flex: 1,
    padding: 12,
    borderRightWidth: 1,
    borderRightColor: "#dee2e6",
    alignItems: "center",
    justifyContent: "center"
  },
  skillsSkillText: {
    fontSize: 14,
    color: "#212529"
  },
  skillsCellText: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#212529"
  },
  lessonsContainer: {
    marginVertical: 20
  },
  lessonsTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#212529",
    marginBottom: 16,
    textAlign: "center"
  },
  lessonGroup: {
    marginBottom: 24,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    padding: 16
  },
  lessonGroupTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#495057",
    marginBottom: 8
  },
  lessonGroupDescription: {
    fontSize: 14,
    color: "#6c757d",
    marginBottom: 12,
    fontStyle: "italic"
  },
  lessonChild: {
    marginBottom: 12
  },
  lessonChildName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#495057",
    marginBottom: 8
  },
  lessonItem: {
    backgroundColor: "#fff",
    borderRadius: 6,
    padding: 12,
    marginBottom: 6,
    borderLeftWidth: 3,
    borderLeftColor: "#dee2e6"
  },
  lessonContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  lessonName: {
    fontSize: 14,
    color: "#212529",
    flex: 1,
    marginRight: 8
  },
  proficiencyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    // light shadow
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2
  },
  proficiencyText: {
    fontSize: 12,
    fontWeight: "600"
  },
  noteContainer: {
    marginVertical: 16,
    padding: 16,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#007bff"
  },
  noteTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#495057",
    marginBottom: 8
  },
  noteText: {
    fontSize: 14,
    color: "#212529",
    lineHeight: 20
  },
  divider: {
    height: 1,
    backgroundColor: "#dee2e6",
    marginVertical: 20
  },
  profilePhotoContainer: {
    alignItems: "center",
    marginVertical: 16
  },
  profilePhoto: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: "#f0f0f0"
  },
  photoContainer: {
    alignItems: "center",
    marginVertical: 16
  },
  chosenPhoto: {
    width: width * 0.5,
    height: 200,
    borderRadius: 8,
    backgroundColor: "#f0f0f0"
  },
  photoCaption: {
    fontSize: 14,
    color: "#6c757d",
    marginTop: 8,
    textAlign: "center"
  },
  titleContainer: {
    alignItems: "center",
    marginVertical: 20
  },
  reportTitle: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#212529",
    textAlign: "center"
  },
  headingContainer: {
    marginVertical: 16
  },
  heading: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#212529"
  },
  subheadingContainer: {
    marginVertical: 12
  },
  subheading: {
    fontSize: 20,
    fontWeight: "600",
    color: "#495057"
  },
  panelContainer: {
    marginVertical: 16,
    padding: 16,
    backgroundColor: "#e9ecef",
    borderRadius: 8
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#495057",
    marginBottom: 8
  },
  panelText: {
    fontSize: 14,
    color: "#212529",
    lineHeight: 20
  },
  textContainer: {
    marginVertical: 8
  },
  plainText: {
    fontSize: 14,
    color: "#212529",
    lineHeight: 20
  },
  pageBreak: {
    height: 40,
    borderBottomWidth: 2,
    borderBottomColor: "#dee2e6",
    marginVertical: 20
  },
  keyContainer: {
    marginVertical: 16,
    padding: 12,
    backgroundColor: "#fff3cd",
    borderRadius: 8
  },
  keyTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#856404",
    marginBottom: 8
  },
  keyItem: {
    fontSize: 14,
    color: "#856404",
    marginBottom: 4
  },
  lessonGroupNoteContainer: {
    marginBottom: 12,
    padding: 12,
    backgroundColor: "#e8f4f8",
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: "#17a2b8"
  },
  lessonGroupNoteText: {
    fontSize: 14,
    color: "#495057",
    fontStyle: "italic",
    lineHeight: 18
  }
});
